import { useSyncExternalStore } from "react";

export type Theme = "dark" | "light";

const THEME_STORAGE_KEY = "gated:theme";
const THEME_EVENT = "gated:theme-change";

function readTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "light"
      ? "light"
      : "dark";
  } catch {
    return "dark";
  }
}

function setTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // ignore quota / privacy mode
  }
  document.documentElement.classList.toggle("light", theme === "light");
  // CSS color-scheme drives Clerk's `light-dark()` color values, so set
  // it explicitly rather than relying on `prefers-color-scheme`.
  document.documentElement.style.colorScheme = theme;
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: theme }));
}

function subscribe(cb: () => void): () => void {
  const onCustom = () => cb();
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY) cb();
  };
  window.addEventListener(THEME_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(THEME_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

// Subscribes to theme changes from anywhere — sidebar toggle, another tab
// (storage event), or programmatic setTheme calls.
export function useTheme(): Theme {
  return useSyncExternalStore(
    subscribe,
    () => readTheme(),
    () => readTheme(),
  );
}

// Convenience hook for components that own a theme toggle. Subscribes to
// the external store for the current value and returns setTheme directly
// — no local state, so no derived-state effect.
export function useLocalTheme(): [Theme, (t: Theme) => void] {
  return [useTheme(), setTheme];
}
